<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog customer-merge-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="customer-merge-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="customer-merge-modal-title">{{ text.detail.mergeModal.title }}</h3>
          <p>{{ text.detail.mergeModal.subtitle }}</p>
        </div>

        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.detail.mergeModal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__content customer-merge-modal__content">
        <form class="app-modal__form" @submit.prevent="emitSubmit">
          <section class="app-modal__section">
            <div class="app-modal__grid two-columns customer-merge-modal__top-row">
              <div class="app-modal-field">
                <span>{{ text.detail.mergeModal.primaryCustomerLabel }}</span>
                <div class="customer-merge-modal__readonly">{{ primaryCustomerName }}</div>
              </div>

              <div class="app-modal-field">
                <span>{{ text.detail.mergeModal.mergeWithLabel }}</span>
                <multiselect
                  v-model="selectedMergeCustomer"
                  :options="mergeCustomerOptions"
                  track-by="id"
                  label="name"
                  :show-labels="false"
                  :searchable="true"
                  :allow-empty="true"
                  :loading="loadingCandidates"
                  :placeholder="text.detail.mergeModal.mergeWithPlaceholder"
                  class="app-modal-select"
                >
                  <template #noResult>
                    <span>{{ text.detail.mergeModal.noOptions }}</span>
                  </template>
                  <template #noOptions>
                    <span>{{ text.detail.mergeModal.noOptions }}</span>
                  </template>
                </multiselect>
              </div>
            </div>

            <div class="customer-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.infoSectionTitle }}</h4>
            </div>

            <div class="customer-merge-modal__table-wrap" v-if="showInformationTable">
              <table class="customer-merge-modal__table">
                <thead>
                  <tr>
                    <th>{{ text.detail.mergeModal.tableHeaders.field }}</th>
                    <th>{{ primaryCustomerName }}</th>
                    <th>{{ mergeCustomerName }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in informationRows" :key="row.key">
                    <td>{{ row.label }}</td>
                    <td>
                      <label class="customer-merge-modal__choice">
                        <span
                          v-if="hasValue(row.primaryValue)"
                          class="customer-merge-modal__checkbox"
                          :class="{ 'is-checked': row.source === 'primary' }"
                          role="button"
                          tabindex="0"
                          @click="selectInformationSource(row.key, 'primary')"
                          @keydown.enter.prevent="selectInformationSource(row.key, 'primary')"
                        >
                          <i class="fa fa-check" aria-hidden="true"></i>
                        </span>
                        <span v-else class="customer-merge-modal__checkbox customer-merge-modal__checkbox--hidden"></span>
                        <span>{{ row.primaryValue }}</span>
                      </label>
                    </td>
                    <td>
                      <label class="customer-merge-modal__choice">
                        <span
                          v-if="hasValue(row.mergeValue)"
                          class="customer-merge-modal__checkbox"
                          :class="{ 'is-checked': row.source === 'merge' }"
                          role="button"
                          tabindex="0"
                          @click="selectInformationSource(row.key, 'merge')"
                          @keydown.enter.prevent="selectInformationSource(row.key, 'merge')"
                        >
                          <i class="fa fa-check" aria-hidden="true"></i>
                        </span>
                        <span v-else class="customer-merge-modal__checkbox customer-merge-modal__checkbox--hidden"></span>
                        <span>{{ row.mergeValue }}</span>
                      </label>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="customer-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.billingSectionTitle }}</h4>
            </div>

            <div class="customer-merge-modal__warning customer-merge-modal__warning--soft">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              <p>{{ text.detail.mergeModal.billingWarning }}</p>
            </div>

            <div class="customer-merge-modal__list" v-if="showMergeDataSections">
              <label
                v-for="row in billingRows"
                :key="`billing-${row.key}`"
                class="customer-merge-modal__list-item"
                :class="{ 'customer-merge-modal__list-item--locked': row.locked }"
              >
                <span
                  class="customer-merge-modal__checkbox"
                  :class="{ 'is-checked': row.checked, 'is-disabled': row.locked }"
                  role="button"
                  tabindex="0"
                  @click="toggleBillingRow(row.key)"
                  @keydown.enter.prevent="toggleBillingRow(row.key)"
                >
                  <i class="fa fa-check" aria-hidden="true"></i>
                </span>
                <span class="customer-merge-modal__list-main">{{ row.label }}</span>
                <small>{{ row.originLabel }}</small>
              </label>
            </div>

            <div class="customer-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.contactsSectionTitle }}</h4>
            </div>

            <div class="customer-merge-modal__list" v-if="showMergeDataSections">
              <label
                v-for="row in contactsRows"
                :key="`contact-${row.key}`"
                class="customer-merge-modal__list-item"
              >
                <span
                  class="customer-merge-modal__checkbox"
                  :class="{ 'is-checked': row.checked }"
                  role="button"
                  tabindex="0"
                  @click="toggleContactRow(row.key)"
                  @keydown.enter.prevent="toggleContactRow(row.key)"
                >
                  <i class="fa fa-check" aria-hidden="true"></i>
                </span>
                <span class="customer-merge-modal__list-main">{{ row.label }}</span>
                <small>{{ row.originLabel }}</small>
              </label>
            </div>

            <div class="customer-merge-modal__separator">
              <h4>{{ text.detail.mergeModal.relatedSectionTitle }}</h4>
            </div>

            <table class="customer-merge-modal__table customer-merge-modal__table--summary" v-if="showMergeDataSections">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ primaryCustomerName }}</th>
                  <th>{{ mergeCustomerName }}</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="metric in relatedSummaryRows" :key="metric.key">
                  <td>{{ metric.label }}</td>
                  <td>{{ metric.primary }}</td>
                  <td>{{ metric.merge }}</td>
                  <td>{{ metric.total }}</td>
                </tr>
              </tbody>
            </table>

            <div class="customer-merge-modal__separator customer-merge-modal__separator--warning"></div>

            <div class="customer-merge-modal__warning customer-merge-modal__warning--info">
              <i class="fa fa-info-circle" aria-hidden="true"></i>
              <p>{{ text.detail.mergeModal.irreversibleWarning }}</p>
            </div>

            <div class="app-modal-field customer-merge-modal__confirm">
              <span>{{ text.detail.mergeModal.confirmLabel }}</span>
              <input
                v-model="confirmationValue"
                type="text"
                class="form-control"
                :placeholder="text.detail.mergeModal.confirmPlaceholder"
              >
            </div>
          </section>

          <footer class="app-modal__footer">
            <div class="app-modal__footer-right">
              <button type="button" class="btn btn-light" :disabled="submitting" @click="emitClose">
                {{ text.detail.mergeModal.actions.cancel }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="!canSubmitMerge || submitting">
                {{ submitting ? text.detail.mergeModal.actions.submitting : text.detail.mergeModal.actions.submit }}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import customersService from '@/services/customers';
import salesService from '@/services/sales';
import salesFlatRatesService from '@/services/salesFlatRates';
import salesMultiCustomerService from '@/services/salesMultiCustomer';
import invoicesService from '@/services/invoices';
import quotesService from '@/services/quotes';
import paymentPlansService from '@/services/paymentPlans';
import customerCatalogsService from '@/services/customerCatalogs';
import generalCatalogsService from '@/services/generalCatalogs';
import areasService from '@/services/areas';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { normalizeCatalogOptions, normalizeGeoOptions } from '@/utils/catalogOptions';
import { formatDateShort } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

const toTextValue = (value) => String(value ?? '').trim();

const hasContent = (value) => toTextValue(value) !== '';

const buildBillingLabel = (row = {}) => {
  const name = toTextValue(row?.name || row?.business_name || row?.fiscal_name);
  const rfc = toTextValue(row?.rfc);
  if (name !== '' && rfc !== '') {
    return `${name} (${rfc})`;
  }

  return name || rfc || 'Dato de facturación';
};

const buildContactLabel = (row = {}) => {
  const name = toTextValue(row?.name || `${row?.first_name || ''} ${row?.last_name || ''}`);
  const email = toTextValue(row?.email);
  if (name !== '' && email !== '') {
    return `${name} (${email})`;
  }

  return name || email || 'Persona de contacto';
};

const isBillingLocked = (row = {}) => {
  const candidates = [
    row?.used,
    row?.used_in_invoices,
    row?.has_invoices,
    row?.invoices_count,
    row?.cfdi_count,
  ];

  return candidates.some((value) => Number(value || 0) > 0);
};

const getDistinctCount = (rows = []) => {
  const ids = new Set();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = Number(row?.sale_id || row?.id || 0);
    if (id > 0) {
      ids.add(id);
    }
  });
  return ids.size;
};

export default {
  name: 'CustomerMergeModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    primaryCustomer: {
      type: Object,
      default: null,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text,
      loadingCandidates: false,
      loadingMergeData: false,
      loadingCatalogs: false,
      mergeCandidates: [],
      selectedMergeCustomerId: 0,
      mergeCustomerDetail: null,
      informationRows: [],
      billingRows: [],
      contactsRows: [],
      customerTypeOptions: [],
      sizeOptions: [],
      sectorOptions: [],
      originOptions: [],
      countryOptions: [],
      stateOptions: [],
      areaOptions: [],
      primarySummary: {
        businesses: 0,
        payments: 0,
        invoices: 0,
        quotes: 0,
      },
      mergeSummary: {
        businesses: 0,
        payments: 0,
        invoices: 0,
        quotes: 0,
      },
      confirmationValue: '',
    };
  },
  computed: {
    primaryCustomerId() {
      return Number(this.primaryCustomer?.id || 0);
    },
    primaryCustomerName() {
      const value = toTextValue(this.primaryCustomer?.name);
      return value !== '' ? value : this.text.common.noName;
    },
    mergeCustomerName() {
      const value = toTextValue(this.mergeCustomerDetail?.name || this.selectedMergeCustomer?.name);
      return value !== '' ? value : this.text.detail.mergeModal.mergeCustomerFallback;
    },
    selectedMergeCustomer: {
      get() {
        return this.mergeCustomerOptions.find((row) => Number(row?.id || 0) === this.selectedMergeCustomerId) || null;
      },
      set(option) {
        this.selectedMergeCustomerId = Number(option?.id || 0);
      },
    },
    mergeCustomerId() {
      return Number(this.selectedMergeCustomerId || 0);
    },
    mergeCustomerOptions() {
      return this.mergeCandidates.filter((row) => Number(row?.id || 0) > 0 && Number(row?.id || 0) !== this.primaryCustomerId);
    },
    showInformationTable() {
      return this.mergeCustomerId > 0 && this.informationRows.length > 0;
    },
    showMergeDataSections() {
      return this.mergeCustomerId > 0;
    },
    relatedSummaryRows() {
      const rows = [
        { key: 'businesses', label: this.text.detail.mergeModal.summaryLabels.businesses },
        { key: 'payments', label: this.text.detail.mergeModal.summaryLabels.payments },
        { key: 'invoices', label: this.text.detail.mergeModal.summaryLabels.invoices },
        { key: 'quotes', label: this.text.detail.mergeModal.summaryLabels.quotes },
      ];

      return rows.map((row) => {
        const primary = Number(this.primarySummary?.[row.key] || 0);
        const merge = Number(this.mergeSummary?.[row.key] || 0);
        return {
          ...row,
          primary,
          merge,
          total: primary + merge,
        };
      });
    },
    canSubmitMerge() {
      return this.mergeCustomerId > 0 && toTextValue(this.confirmationValue).toLowerCase() === 'fusionar';
    },
  },
  watch: {
    show(value) {
      if (!value) {
        return;
      }

      this.handleModalOpen();
    },
    selectedMergeCustomerId() {
      this.loadMergeData();
    },
  },
  methods: {
    hasValue(value) {
      return hasContent(value);
    },
    buildInformationFields() {
      return [
        { key: 'name', label: this.text.modal.form.general.fields.name, sourceKey: 'name' },
        { key: 'admissionDate', label: this.text.modal.form.general.fields.admissionDate, sourceKey: 'since_date' },
        { key: 'website', label: this.text.modal.form.general.fields.website, sourceKey: 'website' },
        { key: 'status', label: this.text.modal.form.general.fields.status, sourceKey: 'active' },
        { key: 'comment', label: this.text.modal.form.general.fields.comment, sourceKey: 'description' },
        { key: 'email', label: this.text.modal.form.contact.fields.email, sourceKey: 'email' },
        { key: 'phone', label: this.text.modal.form.contact.fields.phone, sourceKey: 'phone' },
        { key: 'additionalPhone', label: this.text.modal.form.contact.fields.additionalPhone, sourceKey: 'fax' },
        { key: 'type', label: this.text.modal.form.classification.fields.type, sourceKey: 'type' },
        { key: 'size', label: this.text.modal.form.classification.fields.size, sourceKey: 'size' },
        { key: 'sector', label: this.text.modal.form.classification.fields.sector, sourceKey: 'sector' },
        { key: 'origin', label: this.text.modal.form.classification.fields.origin, sourceKey: 'source' },
        { key: 'area', label: this.text.modal.form.classification.fields.area, sourceKey: 'area_id' },
        { key: 'useBillingAddress', label: this.text.modal.form.address.fields.useBillingAddress, sourceKey: 'use_billing_address' },
        { key: 'address', label: this.text.modal.form.address.fields.address, sourceKey: 'address' },
        { key: 'country', label: this.text.modal.form.address.fields.country, sourceKey: 'country' },
        { key: 'state', label: this.text.modal.form.address.fields.state, sourceKey: 'state' },
        { key: 'city', label: this.text.modal.form.address.fields.city, sourceKey: 'city' },
        { key: 'zipCode', label: this.text.modal.form.address.fields.zipCode, sourceKey: 'zipcode' },
        { key: 'creditDays', label: this.text.modal.form.financial.fields.creditDays, sourceKey: 'credit_days' },
      ];
    },
    resolveOptionLabel(rawValue, options = []) {
      const normalizedRaw = toTextValue(rawValue).toLowerCase();
      if (normalizedRaw === '') {
        return '';
      }

      const match = (Array.isArray(options) ? options : []).find((option) => {
        const candidates = [
          toTextValue(option?.value),
          toTextValue(option?.meta?.id),
          toTextValue(option?.meta?.code),
          toTextValue(option?.meta?.name),
          toTextValue(option?.label),
        ].map((value) => value.toLowerCase());

        return candidates.includes(normalizedRaw);
      });

      return toTextValue(match?.label);
    },
    resolveCustomerFieldValue(customer, field) {
      const sourceKey = toTextValue(field?.sourceKey || field?.key);
      if (sourceKey === '') {
        return '';
      }

      if (sourceKey === 'active') {
        return Number(customer?.active ?? 0) === 1
          ? this.text.modal.form.general.fields.active
          : this.text.modal.form.general.fields.inactive;
      }

      if (sourceKey === 'since_date') {
        return formatDateShort(customer?.since_date, { fallback: '' });
      }

      if (sourceKey === 'use_billing_address') {
        return Number(customer?.use_billing_address ?? 0) === 1 ? 'Si' : 'No';
      }

      if (sourceKey === 'type') {
        return this.resolveOptionLabel(customer?.type, this.customerTypeOptions) || toTextValue(customer?.type);
      }

      if (sourceKey === 'size') {
        return this.resolveOptionLabel(customer?.size, this.sizeOptions) || toTextValue(customer?.size);
      }

      if (sourceKey === 'sector') {
        return this.resolveOptionLabel(customer?.sector, this.sectorOptions) || toTextValue(customer?.sector);
      }

      if (sourceKey === 'source') {
        return this.resolveOptionLabel(customer?.source, this.originOptions) || toTextValue(customer?.source);
      }

      if (sourceKey === 'country') {
        return this.resolveOptionLabel(customer?.country, this.countryOptions) || toTextValue(customer?.country);
      }

      if (sourceKey === 'state') {
        return this.resolveOptionLabel(customer?.state, this.stateOptions) || toTextValue(customer?.state);
      }

      if (sourceKey === 'area_id') {
        const areaId = Number(customer?.area_id || customer?.area?.id || 0);
        if (areaId <= 0) {
          return 'Sin Area asignada';
        }

        return this.resolveOptionLabel(areaId, this.areaOptions)
          || toTextValue(customer?.area?.name)
          || toTextValue(customer?.area_id);
      }

      return toTextValue(customer?.[sourceKey]);
    },
    async handleModalOpen() {
      this.confirmationValue = '';
      this.selectedMergeCustomerId = 0;
      this.mergeCustomerDetail = null;
      this.informationRows = [];
      this.billingRows = [];
      this.contactsRows = [];
      this.mergeSummary = {
        businesses: 0,
        payments: 0,
        invoices: 0,
        quotes: 0,
      };

      await Promise.all([
        this.loadCatalogOptions(),
        this.loadMergeCandidates(),
        this.loadSummaryForPrimary(),
      ]);
    },
    async loadCatalogOptions() {
      if (this.loadingCatalogs) {
        return;
      }

      this.loadingCatalogs = true;
      try {
        const [typesResult, sizesResult, sectorsResult, originsResult, countriesResult, statesResult, areasResult] = await Promise.allSettled([
          customerCatalogsService.getCustomerTypes(),
          customerCatalogsService.getCustomerSizes(),
          customerCatalogsService.getCustomerSectors(),
          customerCatalogsService.getCustomerOrigins(),
          generalCatalogsService.getCountries(),
          generalCatalogsService.getStates(),
          areasService.index(),
        ]);

        this.customerTypeOptions = normalizeCatalogOptions(typesResult.status === 'fulfilled' ? typesResult.value?.data : [], { useCodeInLabel: false });
        this.sizeOptions = normalizeCatalogOptions(sizesResult.status === 'fulfilled' ? sizesResult.value?.data : [], { useCodeInLabel: false });
        this.sectorOptions = normalizeCatalogOptions(sectorsResult.status === 'fulfilled' ? sectorsResult.value?.data : [], { useCodeInLabel: false });
        this.originOptions = normalizeCatalogOptions(originsResult.status === 'fulfilled' ? originsResult.value?.data : [], { useCodeInLabel: false });
        this.countryOptions = normalizeGeoOptions(countriesResult.status === 'fulfilled' ? countriesResult.value?.data : []);
        this.stateOptions = normalizeGeoOptions(statesResult.status === 'fulfilled' ? statesResult.value?.data : []);
        this.areaOptions = normalizeCatalogOptions(areasResult.status === 'fulfilled' ? areasResult.value?.data : [], { useCodeInLabel: false });
      } catch (error) {
        this.customerTypeOptions = [];
        this.sizeOptions = [];
        this.sectorOptions = [];
        this.originOptions = [];
        this.countryOptions = [];
        this.stateOptions = [];
        this.areaOptions = [];
      } finally {
        this.loadingCatalogs = false;
      }
    },
    async loadMergeCandidates() {
      this.loadingCandidates = true;
      try {
        const response = await customersService.selectors();
        this.mergeCandidates = extractCollection(response);
      } catch (error) {
        this.mergeCandidates = [];
      } finally {
        this.loadingCandidates = false;
      }
    },
    async loadSummaryForPrimary() {
      if (this.primaryCustomerId <= 0) {
        this.primarySummary = {
          businesses: 0,
          payments: 0,
          invoices: 0,
          quotes: 0,
        };
        return;
      }

      this.primarySummary = await this.resolveCustomerSummary(this.primaryCustomerId);
    },
    async loadMergeData() {
      const mergeId = this.mergeCustomerId;
      if (mergeId <= 0) {
        this.mergeCustomerDetail = null;
        this.informationRows = [];
        this.billingRows = [];
        this.contactsRows = [];
        this.mergeSummary = {
          businesses: 0,
          payments: 0,
          invoices: 0,
          quotes: 0,
        };
        return;
      }

      this.loadingMergeData = true;

      try {
        const [detailResponse, contributorsResponse, contactsResponse, summary] = await Promise.all([
          customersService.show(mergeId),
          customersService.contributorsIndex(mergeId),
          customersService.personsIndex(mergeId),
          this.resolveCustomerSummary(mergeId),
        ]);

        this.mergeCustomerDetail = extractPayload(detailResponse);
        const contributors = extractCollection(contributorsResponse);
        const contacts = extractCollection(contactsResponse);
        this.mergeSummary = summary;

        this.informationRows = this.buildInformationFields().map((field) => {
          const primaryValue = this.resolveCustomerFieldValue(this.primaryCustomer || {}, field);
          const mergeValue = this.resolveCustomerFieldValue(this.mergeCustomerDetail || {}, field);
          const source = !hasContent(primaryValue) && hasContent(mergeValue) ? 'merge' : 'primary';

          return {
            key: field.key,
            label: field.label,
            primaryValue,
            mergeValue,
            source,
          };
        });

        this.billingRows = contributors.map((row) => {
          const locked = isBillingLocked(row);
          return {
            key: `merge-${Number(row?.id || 0)}`,
            sourceId: Number(row?.id || 0),
            sourceType: 'merge',
            checked: true,
            locked,
            label: buildBillingLabel(row),
            originLabel: this.mergeCustomerName,
          };
        });

        this.contactsRows = contacts.map((row) => {
          return {
            key: `merge-${Number(row?.id || 0)}`,
            sourceId: Number(row?.id || 0),
            sourceType: 'merge',
            checked: true,
            label: buildContactLabel(row),
            originLabel: this.mergeCustomerName,
          };
        });
      } catch (error) {
        this.mergeCustomerDetail = null;
        this.informationRows = [];
        this.billingRows = [];
        this.contactsRows = [];
      } finally {
        this.loadingMergeData = false;
      }
    },
    async resolveCustomerSummary(customerId) {
      const id = Number(customerId || 0);
      if (id <= 0) {
        return {
          businesses: 0,
          payments: 0,
          invoices: 0,
          quotes: 0,
        };
      }

      const [salesResult, flatResult, multiResult, paymentsResult, invoicesResult, quotesResult] = await Promise.allSettled([
        salesService.index({ customer_id: id }, { skipLoading: true }),
        salesFlatRatesService.index({ customer_id: id }, { skipLoading: true }),
        salesMultiCustomerService.index({ customer_id: id }, { skipLoading: true }),
        paymentPlansService.index({ customer_id: id }, { skipLoading: true }),
        invoicesService.index({ customer_id: id }, { skipLoading: true }),
        quotesService.selectors({ customer_id: id }, { skipLoading: true }),
      ]);

      const salesRows = salesResult.status === 'fulfilled' ? extractCollection(salesResult.value) : [];
      const flatRows = flatResult.status === 'fulfilled' ? extractCollection(flatResult.value) : [];
      const multiRows = multiResult.status === 'fulfilled' ? extractCollection(multiResult.value) : [];
      const paymentRows = paymentsResult.status === 'fulfilled' ? extractCollection(paymentsResult.value) : [];
      const invoiceRows = invoicesResult.status === 'fulfilled' ? extractCollection(invoicesResult.value) : [];
      const quoteRows = quotesResult.status === 'fulfilled' ? extractCollection(quotesResult.value) : [];

      return {
        businesses: getDistinctCount([...salesRows, ...flatRows, ...multiRows]),
        payments: Array.isArray(paymentRows) ? paymentRows.length : 0,
        invoices: Array.isArray(invoiceRows) ? invoiceRows.length : 0,
        quotes: Array.isArray(quoteRows) ? quoteRows.length : 0,
      };
    },
    selectInformationSource(fieldKey, source) {
      this.informationRows = this.informationRows.map((row) => {
        if (row.key !== fieldKey) {
          return row;
        }

        if (source === 'merge' && !hasContent(row.mergeValue) && hasContent(row.primaryValue)) {
          return row;
        }

        if (source === 'primary' && !hasContent(row.primaryValue) && hasContent(row.mergeValue)) {
          return row;
        }

        return {
          ...row,
          source,
        };
      });
    },
    toggleBillingRow(rowKey) {
      this.billingRows = this.billingRows.map((row) => {
        if (row.key !== rowKey || row.locked) {
          return row;
        }

        return {
          ...row,
          checked: !row.checked,
        };
      });
    },
    toggleContactRow(rowKey) {
      this.contactsRows = this.contactsRows.map((row) => {
        if (row.key !== rowKey) {
          return row;
        }

        return {
          ...row,
          checked: !row.checked,
        };
      });
    },
    emitClose() {
      if (this.submitting) {
        return;
      }

      this.$emit('close');
    },
    emitSubmit() {
      if (!this.canSubmitMerge || this.submitting) {
        return;
      }

      const payload = {
        primary_customer_id: this.primaryCustomerId,
        merge_customer_id: this.mergeCustomerId,
        information_source: this.informationRows.reduce((carry, row) => {
          carry[row.key] = row.source;
          return carry;
        }, {}),
        contributors: this.billingRows
          .filter((row) => row.checked)
          .map((row) => ({
            id: row.sourceId,
            source: row.sourceType,
          })),
        contacts: this.contactsRows
          .filter((row) => row.checked)
          .map((row) => ({
            id: row.sourceId,
            source: row.sourceType,
          })),
      };

      this.$emit('submit', payload);
    },
  },
};
</script>

<style scoped>
.customer-merge-modal__dialog {
  max-width: 1080px;
  height: auto;
  max-height: calc(100vh - 48px);
}

.customer-merge-modal__content {
  min-height: auto;
}

.customer-merge-modal__top-row {
  align-items: end;
}

.customer-merge-modal__readonly {
  min-height: 38px;
  border: 1px solid rgba(148, 163, 184, 0.45);
  border-radius: 8px;
  background: #f8fafc;
  padding: 8px 10px;
  color: #1e293b;
  display: flex;
  align-items: center;
}

.customer-merge-modal__separator {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px dashed rgba(148, 163, 184, 0.5);
}

.customer-merge-modal__separator h4 {
  margin: 0;
  font-size: 15px;
  color: #0f172a;
}

.customer-merge-modal__table-wrap {
  margin-top: 10px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  overflow: hidden;
}

.customer-merge-modal__table {
  width: 100%;
  border-collapse: collapse;
  background: #ffffff;
}

.customer-merge-modal__table th,
.customer-merge-modal__table td {
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  padding: 9px 10px;
  font-size: 13px;
  vertical-align: top;
}

.customer-merge-modal__table th {
  background: #f8fafc;
  color: #334155;
  font-weight: 700;
}

.customer-merge-modal__table th:first-child,
.customer-merge-modal__table td:first-child {
  width: 230px;
  min-width: 230px;
  max-width: 230px;
}

.customer-merge-modal__choice {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.customer-merge-modal__checkbox {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 1px solid rgba(100, 116, 139, 0.62);
  background: #ffffff;
  color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  margin-top: 2px;
}

.customer-merge-modal__checkbox i {
  font-size: 10px;
}

.customer-merge-modal__checkbox.is-checked {
  border-color: #0070cd;
  background: #0070cd;
  color: #ffffff;
}

.customer-merge-modal__checkbox.is-disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.customer-merge-modal__checkbox--hidden {
  visibility: hidden;
  pointer-events: none;
}

.customer-merge-modal__warning {
  margin-top: 10px;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.customer-merge-modal__warning p {
  margin: 0;
  font-size: 12px;
  line-height: 18px;
}

.customer-merge-modal__warning--soft {
  border: 1px solid rgba(14, 116, 144, 0.3);
  background: #ecfeff;
}

.customer-merge-modal__warning--soft i,
.customer-merge-modal__warning--soft p {
  color: #0e7490;
}

.customer-merge-modal__warning--danger {
  border: 1px solid rgba(185, 28, 28, 0.35);
  background: #fff1f2;
}

.customer-merge-modal__warning--danger i,
.customer-merge-modal__warning--danger p {
  color: #b91c1c;
}

.customer-merge-modal__warning--info {
  border: 1px solid rgba(30, 64, 175, 0.25);
  background: #eff6ff;
}

.customer-merge-modal__warning--info i,
.customer-merge-modal__warning--info p {
  color: #1d4ed8;
}

.customer-merge-modal__list {
  margin-top: 10px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.customer-merge-modal__list-item {
  margin: 0;
  border: 1px solid rgba(148, 163, 184, 0.34);
  border-radius: 10px;
  padding: 9px 10px;
  background: #ffffff;
  display: grid;
  grid-template-columns: 18px 1fr;
  column-gap: 8px;
  row-gap: 2px;
  align-items: center;
}

.customer-merge-modal__list-item--locked {
  background: #f8fafc;
  border-style: dashed;
}

.customer-merge-modal__list-main {
  font-size: 13px;
  color: #0f172a;
}

.customer-merge-modal__list-item small {
  color: #64748b;
  font-size: 11px;
}

.customer-merge-modal__table--summary td:nth-child(n + 2) {
  text-align: center;
}

.customer-merge-modal__table--summary {
  margin-top: 10px;
  border: 1px solid rgba(148, 163, 184, 0.32);
  border-radius: 10px;
  border-collapse: separate;
  border-spacing: 0;
}

.customer-merge-modal__separator--warning {
  margin-top: 14px;
}

.customer-merge-modal__confirm {
  margin-top: 12px;
}

@media (max-width: 960px) {
  .customer-merge-modal__list {
    grid-template-columns: 1fr;
  }

  .customer-merge-modal__table-wrap {
    overflow-x: auto;
  }

  .customer-merge-modal__table {
    min-width: 760px;
  }
}
</style>
